
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import SlidingDrawer from '../../components/SlidingDrawer';

const ICONS = {
    search: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
    calendar: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    authority: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
    noAgendas: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>,
    time: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
};

const CommitteeManager = () => {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);

    useEffect(() => {
        fetchMeetings();
    }, []);

    const fetchMeetings = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/committee-meetings/secretary`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMeetings(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [uploadingReport, setUploadingReport] = useState(false);

    const handleFileUpload = async (e, type, id) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('token');
        const setLoading = type === 'doc' ? setUploadingDoc : setUploadingReport;

        setLoading(true);
        try {
            const uploadRes = await axios.post(`${API_BASE_URL}/api/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (type === 'doc') {
                await axios.post(`${API_BASE_URL}/api/committee-meetings/${id}/support-docs`,
                    { title: file.name, url: uploadRes.data.url },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                Swal.fire('Success', 'Supportive document uploaded successfully', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/committee-meetings/${id}/report`,
                    { url: uploadRes.data.url },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                Swal.fire('Success', 'Meeting report submitted for approval', 'success');
            }

            fetchMeetings();
            setIsDrawerOpen(false);
        } catch (err) {
            console.error('Upload Error:', err);
            Swal.fire('Error', 'Failed to upload file. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const isMeetingPassed = (meeting) => {
        if (!meeting.date) return false;
        const meetingDate = new Date(meeting.date);
        const now = new Date();

        if (meetingDate > now && meetingDate.toDateString() !== now.toDateString()) return false;
        if (meetingDate < now && meetingDate.toDateString() !== now.toDateString()) return true;

        // Same day logic
        if (meeting.time) {
            try {
                const [time, period] = meeting.time.split(' ');
                const [hours, minutes] = time.split(':');
                let hr = parseInt(hours);
                if (period === 'PM' && hr < 12) hr += 12;
                if (period === 'AM' && hr === 12) hr = 0;

                const mTime = new Date(meetingDate);
                mTime.setHours(hr, parseInt(minutes), 0);
                return now > mTime;
            } catch (e) {
                return now > meetingDate;
            }
        }
        return now > meetingDate;
    };

    const getStatusStyles = (m) => {
        if (m.report?.status === 'REJECTED') {
            return { bg: '#FFEBEE', text: '#D32F2F', dot: '#D32F2F', label: 'CORRECTION REQ' };
        }
        const passed = isMeetingPassed(m);
        if (passed) return { bg: '#E8F5E9', text: '#2E7D32', dot: '#2E7D32', label: 'COMPLETED' };
        return { bg: '#FFF3E0', text: '#E65100', dot: '#E65100', label: 'SCHEDULED' };
    };

    const filteredMeetings = meetings.filter(m =>
        (m.title || '').toLowerCase().includes(filter.toLowerCase()) ||
        (m.committee?.name || '').toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', fontFamily: '"Outfit", sans-serif' }}>
            <input
                type="file"
                id="support-doc-picker"
                style={{ display: 'none' }}
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e, 'doc', selectedMeeting?._id)}
            />
            <input
                type="file"
                id="report-picker"
                style={{ display: 'none' }}
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e, 'report', selectedMeeting?._id)}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px' }}>
                <div>
                    <h2 style={{ color: '#2C1A10', margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800' }}>Committee Manager</h2>
                    <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>Administrative Oversight & Legislative Session Governance Protocol.</p>
                </div>
                <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', zIndex: 2 }}>
                        {ICONS.search}
                    </div>
                    <input
                        type="text"
                        placeholder="Search by Committee or Title..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '16px 20px 16px 48px',
                            borderRadius: '16px',
                            border: '1.5px solid #E5E5E5',
                            fontSize: '15px',
                            boxSizing: 'border-box',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fff',
                            color: '#333'
                        }}
                    />
                </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '28px', boxShadow: '0 15px 45px rgba(0,0,0,0.03)', overflow: 'hidden', border: '1px solid #F0F0F0', minHeight: '500px' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#FDF8F3' }}>
                        <tr style={{ color: '#888', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                            <th style={{ padding: '25px 30px' }}>Committee Entity</th>
                            <th style={{ padding: '25px 30px' }}>Session Details</th>
                            <th style={{ padding: '25px 30px' }}>Temporal Matrix</th>
                            <th style={{ padding: '25px 30px' }}>Operational Status</th>
                            <th style={{ padding: '25px 30px', textAlign: 'right' }}>Directives</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" style={{ padding: '80px', textAlign: 'center', color: '#888', fontWeight: '700' }}>SYNCHRONIZING REPOSITORY...</td></tr>
                        ) : filteredMeetings.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: '100px 40px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                        <div style={{
                                            width: '120px', height: '120px', backgroundColor: '#FDF8F3', borderRadius: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '30px'
                                        }}>
                                            {ICONS.calendar}
                                        </div>
                                        <h3 style={{ color: '#2C1A10', margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800' }}>Registry Clear: No Meetings</h3>
                                        <p style={{ color: '#888', fontSize: '16px', margin: 0, maxWidth: '350px', lineHeight: '1.6' }}>
                                            No committee records matched your current filter criteria or the queue is empty.
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredMeetings.map(m => {
                                const styles = getStatusStyles(m);
                                return (
                                    <tr key={m._id} style={{ borderBottom: '1px solid #F5F5F5', transition: 'background 0.2s' }}>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '16px', fontWeight: '800', color: '#2C1A10' }}>{m.committee?.name || 'Unknown Committee'}</div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ display: 'flex' }}>{ICONS.authority}</span>
                                                Chair: {m.chairperson?.firstName} {m.chairperson?.lastName}
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#444' }}>{m.title}</div>
                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Session ID: #{m._id.slice(-6).toUpperCase()}</div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#444', fontWeight: '600', fontSize: '14px' }}>
                                                <span style={{ color: '#C0722C' }}>{ICONS.calendar}</span>
                                                {new Date(m.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {ICONS.time} {m.time || 'Time TBD'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', backgroundColor: styles.bg, borderRadius: '12px', border: `1px solid ${styles.text}20`
                                            }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: styles.dot }}></div>
                                                <span style={{ color: styles.text, fontWeight: '800', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {styles.label}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => { setSelectedMeeting(m); setIsDrawerOpen(true); }}
                                                style={{
                                                    padding: '12px 24px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 10px rgba(44, 26, 16, 0.15)'
                                                }}
                                                onMouseOver={(e) => e.target.style.backgroundColor = '#C0722C'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = '#2C1A10'}
                                            >
                                                Details Action
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title="Committee Meeting Dossier"
            >
                {selectedMeeting && (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        <div style={{ padding: '25px', backgroundColor: '#FDF8F3', borderRadius: '20px', border: '1px solid #F0E8DB', marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Session Subject Matter</div>
                                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#2C1A10', lineHeight: '1.4' }}>{selectedMeeting.title}</div>
                                </div>
                                <div style={{
                                    padding: '6px 12px',
                                    backgroundColor: getStatusStyles(selectedMeeting).bg,
                                    color: getStatusStyles(selectedMeeting).text,
                                    borderRadius: '10px',
                                    fontSize: '10px',
                                    fontWeight: '800',
                                    textTransform: 'uppercase'
                                }}>
                                    {getStatusStyles(selectedMeeting).label}
                                </div>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Committee Details</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Assigned Committee</div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{selectedMeeting.committee?.name}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Presiding Chairperson</div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{selectedMeeting.chairperson?.firstName} {selectedMeeting.chairperson?.lastName}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Scheduled Date</div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{new Date(selectedMeeting.date).toLocaleDateString()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Session Time</div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{selectedMeeting.time || 'TBD'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '15px' }}>Meeting Directives</div>
                            <div style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
                                {!isMeetingPassed(selectedMeeting) ? (
                                    <>
                                        {(!selectedMeeting.supportDocuments || selectedMeeting.supportDocuments.length === 0) && (
                                            <button
                                                onClick={() => document.getElementById('support-doc-picker').click()}
                                                disabled={uploadingDoc}
                                                style={{
                                                    padding: '18px', backgroundColor: '#FFF8E1', color: '#F57C00', border: '1.5px solid #FFE0B2', borderRadius: '16px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                                                }}
                                                onMouseOver={(e) => e.target.style.backgroundColor = '#FFF3E0'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = '#FFF8E1'}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
                                                {uploadingDoc ? 'Uploading...' : 'Upload Supportive Document'}
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Report Upload Logic: Only if NO report OR Rejected */}
                                        {(!selectedMeeting.report?.url || selectedMeeting.report?.status === 'REJECTED') ? (
                                            <button
                                                onClick={() => document.getElementById('report-picker').click()}
                                                disabled={uploadingReport}
                                                style={{
                                                    padding: '18px',
                                                    backgroundColor: selectedMeeting.report?.status === 'REJECTED' ? '#D32F2F' : '#2C1A10',
                                                    color: 'white', border: 'none', borderRadius: '16px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                                                }}
                                                onMouseOver={(e) => e.target.style.backgroundColor = selectedMeeting.report?.status === 'REJECTED' ? '#C62828' : '#C0722C'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = selectedMeeting.report?.status === 'REJECTED' ? '#D32F2F' : '#2C1A10'}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M16 13l-4 4-4-4"></path><path d="M12 17V9"></path></svg>
                                                {uploadingReport
                                                    ? 'Uploading...'
                                                    : (selectedMeeting.report?.status === 'REJECTED' ? 'Upload Corrected Report' : 'Upload Official Report')}
                                            </button>
                                        ) : (
                                            <div style={{ padding: '15px', backgroundColor: '#FFF3E0', border: '1px solid #FFE0B2', borderRadius: '12px', textAlign: 'center', color: '#E65100', fontWeight: '700' }}>
                                                {selectedMeeting.report?.status === 'APPROVED' ? 'Report Finalized & Approved' : 'Report Under Review by Chairperson'}
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Correction Alert Banner */}
                                {selectedMeeting.report?.status === 'REJECTED' && (
                                    <div style={{
                                        marginTop: '20px',
                                        padding: '16px',
                                        backgroundColor: '#FFEBEE',
                                        border: '1px solid #FFCDD2',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'start',
                                        gap: '12px'
                                    }}>
                                        <div style={{ color: '#D32F2F', marginTop: '2px' }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '700', color: '#B71C1C', fontSize: '14px', marginBottom: '4px' }}>Action Required: Correction Requested</div>
                                            <div style={{ fontSize: '13px', color: '#C62828', lineHeight: '1.4' }}>
                                                The Chairperson has returned the report for corrections. Please review the feedback (if any) and upload the revised version above.
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedMeeting.supportDocuments && selectedMeeting.supportDocuments.length > 0 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Attached Dossiers</div>
                                        {selectedMeeting.supportDocuments.map((doc, idx) => (
                                            <a key={idx} href={doc.url} target="_blank" rel="noreferrer" style={{
                                                textDecoration: 'none', padding: '12px 18px', backgroundColor: '#fff', border: '1px solid #F0F0F0', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'
                                            }}>
                                                <div style={{ background: '#FDF8F3', padding: '6px', borderRadius: '8px', color: '#C0722C' }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>
                                                </div>
                                                <span style={{ fontSize: '13px', color: '#2C1A10', fontWeight: '600' }}>{doc.title}</span>
                                            </a>
                                        ))}
                                    </div>
                                )}

                                {selectedMeeting.report?.url && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Final Report</div>
                                        <a href={selectedMeeting.report.url} target="_blank" rel="noreferrer" style={{
                                            textDecoration: 'none', padding: '15px 18px', backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px'
                                        }}>
                                            <div style={{ background: '#fff', padding: '8px', borderRadius: '8px', color: '#2E7D32' }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '14px', color: '#2E7D32', fontWeight: '800' }}>Affirmed Legislative Report</div>
                                                <div style={{ fontSize: '11px', color: '#2E7D32', opacity: 0.8 }}>Status: {selectedMeeting.report.status}</div>
                                            </div>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </SlidingDrawer>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default CommitteeManager;
